import { useEffect, useState } from "react";
import { Truck, CheckCircle2, AlertOctagon, MapPin, Phone } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Link } from "react-router-dom";
import { KpiCard } from "./components/KpiCard";
import { DashboardHeader } from "./components/DashboardHeader";

/**
 * DriverDashboard — pickups and drops for today.
 */
export default function DriverDashboard({ user }) {
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSynced, setLastSynced] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/pickup-drop/my-day")
      .then((r) => setPickups(r.data || []))
      .catch(() =>
        api.get("/pickup-drop")
          .then((r) => setPickups(r.data || []))
          .catch(() => {})
      )
      .finally(() => { setLoading(false); setLastSynced(new Date().toISOString()); });
  };
  useEffect(load, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayItems = pickups.filter((p) => (p.scheduled_at || "").startsWith(today));
  const inTransit = todayItems.filter((p) => p.status === "en_route").length;
  const done = todayItems.filter((p) => p.status === "completed").length;
  const delayed = todayItems.filter(
    (p) => p.status !== "completed" && p.scheduled_at && new Date(p.scheduled_at) < new Date()
  ).length;
  const next = todayItems.find((p) => p.status === "scheduled");

  const kpis = [
    { key: "today-pickups", label: "Today's Pickups", value: todayItems.filter((p) => p.type === "pickup").length, hint: "Scheduled for pickup", Icon: Truck, tone: "frosted", testId: "drv-kpi-pickups" },
    { key: "today-drops", label: "Today's Drops", value: todayItems.filter((p) => p.type === "drop").length, hint: "Scheduled for drop", Icon: Truck, tone: "steel", testId: "drv-kpi-drops" },
    { key: "in-transit", label: "In Transit", value: inTransit, hint: "On the road now", Icon: MapPin, tone: "orange", testId: "drv-kpi-transit" },
    { key: "next", label: "Next Assignment", value: next ? next.customer_name || "—" : "—", hint: next ? fmtDateTime(next.scheduled_at) : "Idle", Icon: Truck, tone: "steel", testId: "drv-kpi-next" },
    { key: "delayed", label: "Delayed", value: delayed, hint: "Past scheduled time", Icon: AlertOctagon, tone: "red", testId: "drv-kpi-delayed" },
    { key: "done", label: "Completed Today", value: done, hint: "Nice", Icon: CheckCircle2, tone: "green", testId: "drv-kpi-done" },
  ];

  const hi = (user?.name || "").split(" ")[0] || "Driver";

  return (
    <div className="space-y-6" data-testid="driver-dashboard-v3">
      <DashboardHeader
        title="On the Road"
        subtitle={`Your pickups, drops and route for today, ${hi}.`}
        range="today"
        onRangeChange={() => {}}
        onRefresh={load}
        onCustomize={() => {}}
        onFilter={() => {}}
        lastSyncedAt={lastSynced}
        loading={loading}
      />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map(({ key, ...rest }) => <KpiCard key={key} {...rest} />)}
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="drv-route">
        <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Route for today</h3>
            <p className="text-[11.5px] text-[#667085] mt-0.5">Scheduled pickups and drops.</p>
          </div>
          <Link to="/dashboard" className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946]" data-testid="drv-route-viewall">
            Refresh →
          </Link>
        </header>
        {todayItems.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">No pickups or drops scheduled for today.</div>
        ) : (
          <ul className="divide-y divide-[#F1FAEE]">
            {todayItems.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-5 py-3" data-testid={`drv-route-${p.id}`}>
                <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${p.type === "pickup" ? "bg-[#a8dadc]/40 text-[#457b9d]" : "bg-[#F1FAEE] text-[#12B76A]"}`}>
                  <Truck size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1d3557] truncate">
                    {p.type === "pickup" ? "Pickup" : "Drop"} · {p.customer_name || p.customer?.name || "—"}
                  </div>
                  <div className="text-[11px] text-[#667085] truncate">
                    {p.address || "—"} · {fmtDateTime(p.scheduled_at)}
                  </div>
                </div>
                {p.phone && (
                  <a
                    href={`tel:${p.phone}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
                    data-testid={`drv-call-${p.id}`}
                  >
                    <Phone size={12} /> Call
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
